import React, { useState, useEffect } from 'react';
import { Box, Heading, Tabs, TabList, TabPanels, Tab, TabPanel, useDisclosure, Flex, IconButton, CloseButton, Button, useToast } from '@chakra-ui/react';
import { Elements } from '@stripe/react-stripe-js';
import { getStripe } from '../services/stripeService';
import PaymentMethodManager from '../components/PaymentMethodManager';
import PaymentHistory from '../components/PaymentHistory';
import { useAuth } from '../contexts/AuthContext';
import { getCompanyPayments } from '../services/paymentService';
import PaymentInitiateModal from '../components/PaymentInitiateModal';

const CompanyPaymentManagement = () => {
  const { currentUser } = useAuth();
  const toast = useToast();
  const [payments, setPayments] = useState([]);
  const [isPaymentModalOpen, setIsPaymentModalOpen] = useState(false);
  const [isLoading, setIsLoading] = useState(true);
  const [refreshHistory, setRefreshHistory] = useState(false);

  useEffect(() => {
    // Set page title
    document.title = 'Payment Management - Synked';
    
    // Fetch payments
    const fetchPayments = async () => {
      if (currentUser?.uid) {
        try {
          const fetchedPayments = await getCompanyPayments(currentUser.uid);
          setPayments(fetchedPayments);
        } catch (error) {
          console.error('Error fetching payments:', error);
        } finally {
          setIsLoading(false);
        }
      }
    };

    fetchPayments();
  }, [currentUser, refreshHistory]);

  const handlePaymentSuccess = (paymentResult) => {
    setIsPaymentModalOpen(false);
    setRefreshHistory(prev => !prev);
    toast({
      title: 'Payment Successful',
      description: 'Payment has been processed successfully.',
      status: 'success',
      duration: 5000,
      isClosable: true,
    });
  };

  return (
    <Box p={6} maxW="1200px" mx="auto">
      <Flex justify="space-between" align="center" mb={6}>
        <Heading size="lg" color="#6c63ff">Payment Management</Heading>
        <Button
          bgGradient="linear(to-r, #6c63ff, #3b82f6)"
          color="white"
          borderRadius="50px"
          boxShadow="0 4px 15px rgba(108, 99, 255, 0.3)"
          _hover={{
            transform: "translateY(-2px)",
            boxShadow: "0 6px 20px rgba(108, 99, 255, 0.4)"
          }}
          onClick={() => setIsPaymentModalOpen(true)}
        >
          Pay Student
        </Button>
      </Flex>
      
      <Box 
        borderRadius="16px" 
        overflow="hidden" 
        boxShadow="0 4px 20px rgba(0,0,0,0.08)" 
        bg="white"
      >
        <Tabs isFitted variant="enclosed" colorScheme="purple">
          <TabList 
            bg="#f5f7ff" 
            borderBottomWidth="2px" 
            borderBottomColor="#eef1ff"
          >
            <Tab 
              fontWeight="500" 
              _selected={{ 
                color: "#6c63ff", 
                borderBottomWidth: "3px", 
                borderBottomColor: "#6c63ff" 
              }}
              py={4}
            >
              Payment Methods
            </Tab>
            <Tab 
              fontWeight="500" 
              _selected={{ 
                color: "#6c63ff", 
                borderBottomWidth: "3px", 
                borderBottomColor: "#6c63ff" 
              }}
              py={4}
            >
              Payment History
            </Tab>
          </TabList>
          <TabPanels>
            <TabPanel p={6}>
              <Elements stripe={getStripe()}>
                <PaymentMethodManager companyId={currentUser?.uid} />
              </Elements>
            </TabPanel>
            <TabPanel p={6}>
              <PaymentHistory 
                payments={payments} 
                isLoading={isLoading} 
                onInitiatePayment={() => setIsPaymentModalOpen(true)}
              />
            </TabPanel>
          </TabPanels>
        </Tabs>
      </Box>

      {/* Payment Modal */}
      {isPaymentModalOpen && (
        <PaymentInitiateModal
          companyId={currentUser?.uid}
          onSuccess={handlePaymentSuccess}
          onClose={() => setIsPaymentModalOpen(false)}
        />
      )}
    </Box>
  );
};

export default CompanyPaymentManagement; 